import { useState, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { accouProApi } from "@/lib/api/client";
import {
  Code, Shield, Zap, Globe, Smartphone, Copy, Play, CheckCircle2,
  Server, Lock, FileText, CreditCard, ShieldCheck, BarChart3, Users,
  ArrowRight, ExternalLink, Cpu, Layers
} from "lucide-react";

interface Endpoint {
  method: "GET" | "POST" | "PUT" | "DELETE";
  path: string;
  description: string;
  params?: string[];
  body?: string;
  response?: string;
}

const ENDPOINT_GROUPS: { name: string; icon: typeof FileText; color: string; endpoints: Endpoint[] }[] = [
  {
    name: "Invoices", icon: FileText, color: "text-primary",
    endpoints: [
      { method: "GET", path: "/invoices", description: "List all invoices with filters", params: ["type", "status", "page", "limit"] },
      { method: "GET", path: "/invoices/:id", description: "Get invoice by ID" },
      { method: "POST", path: "/invoices", description: "Create a new invoice", body: '{ "type": "Sales", "party_name": "...", "amount": 10000, "lines": [...] }' },
      { method: "PUT", path: "/invoices/:id", description: "Update an invoice" },
      { method: "POST", path: "/invoices/:id/payment-link", description: "Generate payment link", body: '{ "gateway": "razorpay", "expires_days": 7 }' },
      { method: "GET", path: "/invoices/:id/pdf", description: "Download invoice as PDF" },
    ],
  },
  {
    name: "Payments", icon: CreditCard, color: "text-success",
    endpoints: [
      { method: "GET", path: "/payments", description: "List payments", params: ["status", "from", "to"] },
      { method: "GET", path: "/payments/:id", description: "Get payment detail" },
      { method: "POST", path: "/payments", description: "Record a payment", body: '{ "invoice_id": "...", "amount": 10000, "method": "UPI" }' },
      { method: "POST", path: "/payments/:id/refund", description: "Initiate refund" },
      { method: "POST", path: "/payments/:id/reconcile", description: "Reconcile with bank" },
    ],
  },
  {
    name: "GST", icon: ShieldCheck, color: "text-warning",
    endpoints: [
      { method: "GET", path: "/gst/returns", description: "Get GST return summary", params: ["period"] },
      { method: "GET", path: "/gst/returns/gstr1", description: "GSTR-1 details" },
      { method: "GET", path: "/gst/returns/gstr3b", description: "GSTR-3B details" },
      { method: "GET", path: "/gst/reconciliation", description: "GST reconciliation data" },
      { method: "GET", path: "/gst/validate/:gstin", description: "Validate GSTIN format" },
      { method: "POST", path: "/gst/e-invoice", description: "Generate E-Invoice IRN", body: '{ "invoice_id": "..." }' },
      { method: "POST", path: "/gst/eway-bill", description: "Generate E-Way Bill", body: '{ "invoice_id": "..." }' },
    ],
  },
  {
    name: "Reports", icon: BarChart3, color: "text-accent",
    endpoints: [
      { method: "GET", path: "/reports/trial-balance", description: "Trial Balance report", params: ["as_of"] },
      { method: "GET", path: "/reports/profit-loss", description: "Profit & Loss statement", params: ["from", "to"] },
      { method: "GET", path: "/reports/balance-sheet", description: "Balance Sheet", params: ["as_of"] },
      { method: "GET", path: "/reports/cash-flow", description: "Cash Flow analysis", params: ["period"] },
      { method: "GET", path: "/reports/receivables-aging", description: "Receivables Aging report" },
      { method: "GET", path: "/reports/gst-summary", description: "GST Summary", params: ["period"] },
      { method: "GET", path: "/reports/ledger/:id", description: "Ledger statement", params: ["from", "to"] },
    ],
  },
  {
    name: "Users", icon: Users, color: "text-purple-500",
    endpoints: [
      { method: "GET", path: "/users/me", description: "Current user info" },
      { method: "GET", path: "/users/profile", description: "User profile data" },
      { method: "PUT", path: "/users/profile", description: "Update profile", body: '{ "name": "...", "phone": "..." }' },
      { method: "GET", path: "/users/preferences", description: "App preferences" },
      { method: "PUT", path: "/users/preferences", description: "Update preferences", body: '{ "theme": "dark", "currency": "INR" }' },
      { method: "GET", path: "/users/companies", description: "User's companies" },
    ],
  },
];

function MethodBadge({ method }: { method: string }) {
  const colors: Record<string, string> = {
    GET: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20",
    POST: "bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/20",
    PUT: "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20",
    DELETE: "bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/20",
  };
  return <span className={`px-2 py-0.5 rounded border text-[10px] font-mono font-bold ${colors[method] || ""}`}>{method}</span>;
}

export default function APIDeveloperPage() {
  const [tab, setTab] = useState("overview");
  const [testEndpoint, setTestEndpoint] = useState("/invoices");
  const [testMethod, setTestMethod] = useState("GET");
  const [testResult, setTestResult] = useState<string | null>(null);
  const [testing, setTesting] = useState(false);

  const baseUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/mobile-api`;

  const handleTest = useCallback(async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const result = await accouProApi.health();
      setTestResult(JSON.stringify(result, null, 2));
      toast.success("API responded successfully");
    } catch (err: any) {
      setTestResult(JSON.stringify({ error: err.message }, null, 2));
      toast.error("API call failed");
    }
    setTesting(false);
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold font-display text-foreground flex items-center gap-2">
          <Code className="h-6 w-6 text-primary" /> API & Mobile Architecture
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          REST API documentation, mobile SDK setup, and developer tools for Android & iOS
        </p>
      </div>

      {/* Architecture Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "API Endpoints", value: ENDPOINT_GROUPS.reduce((s, g) => s + g.endpoints.length, 0), icon: Server, color: "text-primary" },
          { label: "Resource Groups", value: ENDPOINT_GROUPS.length, icon: Layers, color: "text-accent" },
          { label: "Auth", value: "JWT + RLS", icon: Lock, color: "text-success" },
          { label: "Mobile Ready", value: "iOS & Android", icon: Smartphone, color: "text-warning" },
        ].map((s) => (
          <Card key={s.label}>
            <CardContent className="p-3 sm:p-4 flex items-center gap-3">
              <s.icon className={`h-5 w-5 ${s.color}`} />
              <div>
                <p className="text-lg font-bold text-foreground">{s.value}</p>
                <p className="text-xs text-muted-foreground">{s.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="overflow-x-auto">
          <TabsTrigger value="overview">Architecture</TabsTrigger>
          <TabsTrigger value="endpoints">API Reference</TabsTrigger>
          <TabsTrigger value="auth">Authentication</TabsTrigger>
          <TabsTrigger value="mobile">Mobile Setup</TabsTrigger>
          <TabsTrigger value="test">API Tester</TabsTrigger>
        </TabsList>

        {/* ── Architecture Overview ── */}
        <TabsContent value="overview" className="space-y-6 mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2">
                <Layers className="h-4 w-4 text-primary" /> System Architecture
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-4">
                {[
                  {
                    title: "Frontend Layer", icon: Globe, color: "text-primary", bg: "bg-primary/10",
                    items: ["React + TypeScript SPA", "Tailwind CSS + shadcn/ui", "React Router navigation", "Recharts visualization", "Context-based state management"],
                  },
                  {
                    title: "API Layer", icon: Server, color: "text-accent", bg: "bg-accent/10",
                    items: ["Backend Functions (Deno)", "JWT authentication", "CORS-enabled endpoints", "Rate limiting ready", "Versioned API (v1)"],
                  },
                  {
                    title: "Data Layer", icon: Shield, color: "text-success", bg: "bg-success/10",
                    items: ["PostgreSQL database", "Row-Level Security (RLS)", "Real-time subscriptions", "File storage buckets", "Automated backups"],
                  },
                ].map((layer) => (
                  <div key={layer.title} className="p-4 rounded-xl border border-border bg-card">
                    <div className="flex items-center gap-2 mb-3">
                      <div className={`p-2 rounded-lg ${layer.bg}`}>
                        <layer.icon className={`h-4 w-4 ${layer.color}`} />
                      </div>
                      <p className="font-semibold text-foreground text-sm">{layer.title}</p>
                    </div>
                    <ul className="space-y-1.5">
                      {layer.items.map((item) => (
                        <li key={item} className="text-xs text-muted-foreground flex items-center gap-2">
                          <CheckCircle2 className="h-3 w-3 text-success shrink-0" /> {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <div className="grid md:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Smartphone className="h-4 w-4 text-warning" /> Mobile Platform Support
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {[
                  { platform: "Android", version: "API 24+ (Android 7.0+)", tech: "Capacitor + WebView", status: "Ready" },
                  { platform: "iOS", version: "iOS 14+", tech: "Capacitor + WKWebView", status: "Ready" },
                  { platform: "PWA", version: "All modern browsers", tech: "Service Worker + Manifest", status: "Ready" },
                ].map((p) => (
                  <div key={p.platform} className="flex items-center justify-between p-3 rounded-lg bg-muted/30 border border-border">
                    <div>
                      <p className="text-sm font-medium text-foreground">{p.platform}</p>
                      <p className="text-[11px] text-muted-foreground">{p.version} • {p.tech}</p>
                    </div>
                    <Badge className="bg-success/10 text-success border-success/20 text-[10px]">
                      <CheckCircle2 className="h-2.5 w-2.5 mr-1" /> {p.status}
                    </Badge>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Zap className="h-4 w-4 text-accent" /> API Features
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {[
                  "JWT Bearer token authentication",
                  "Row-Level Security on all data",
                  "CORS enabled for cross-origin access",
                  "Standardized JSON response format",
                  "Error codes with descriptive messages",
                  "Pagination support (page, limit)",
                  "Filter & sort query parameters",
                  "Rate limiting (100 req/min)",
                  "Webhook support for async events",
                  "API versioning (v1)",
                ].map((f) => (
                  <div key={f} className="flex items-center gap-2 text-xs text-muted-foreground">
                    <ArrowRight className="h-3 w-3 text-accent shrink-0" /> {f}
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ── API Reference ── */}
        <TabsContent value="endpoints" className="space-y-4 mt-4">
          {ENDPOINT_GROUPS.map((group) => (
            <Card key={group.name}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <group.icon className={`h-4 w-4 ${group.color}`} /> {group.name} API
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {group.endpoints.map((ep, i) => (
                    <div key={i} className="p-3 rounded-lg bg-muted/30 border border-border hover:bg-muted/50 transition-colors">
                      <div className="flex items-start gap-3">
                        <MethodBadge method={ep.method} />
                        <div className="flex-1 min-w-0">
                          <code className="text-xs font-mono text-foreground font-medium">{ep.path}</code>
                          <p className="text-[11px] text-muted-foreground mt-0.5">{ep.description}</p>
                          {ep.params && (
                            <div className="flex flex-wrap gap-1 mt-1.5">
                              {ep.params.map((p) => (
                                <Badge key={p} variant="outline" className="text-[9px] font-mono">{p}</Badge>
                              ))}
                            </div>
                          )}
                          {ep.body && (
                            <pre className="mt-2 p-2 rounded bg-background text-[10px] font-mono text-muted-foreground overflow-x-auto border border-border">
                              {ep.body}
                            </pre>
                          )}
                        </div>
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0 shrink-0"
                          onClick={() => { navigator.clipboard.writeText(`${baseUrl}${ep.path}`); toast.success("Endpoint copied"); }}>
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        {/* ── Authentication ── */}
        <TabsContent value="auth" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2">
                <Lock className="h-4 w-4 text-success" /> Authentication Flow
              </CardTitle>
              <CardDescription className="text-xs">All API requests require a valid JWT Bearer token</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                {[
                  { step: "1", title: "Sign In", desc: "Authenticate with email/password or OAuth to get a session", code: `const { data } = await supabase.auth.signInWithPassword({\n  email: "user@example.com",\n  password: "secure_password"\n});` },
                  { step: "2", title: "Get Token", desc: "Extract the access token from the session", code: `const token = data.session.access_token;` },
                  { step: "3", title: "Call API", desc: "Include the token in the Authorization header", code: `fetch("${baseUrl}/invoices", {\n  headers: {\n    "Authorization": "Bearer " + token,\n    "Content-Type": "application/json"\n  }\n});` },
                  { step: "4", title: "Auto-Refresh", desc: "Tokens auto-refresh. Listen for auth state changes", code: `supabase.auth.onAuthStateChange((event, session) => {\n  // Update stored token\n  if (session) updateToken(session.access_token);\n});` },
                ].map((s) => (
                  <div key={s.step} className="p-4 rounded-lg border border-border">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="w-7 h-7 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center">{s.step}</span>
                      <div>
                        <p className="text-sm font-semibold text-foreground">{s.title}</p>
                        <p className="text-[11px] text-muted-foreground">{s.desc}</p>
                      </div>
                    </div>
                    <pre className="p-3 rounded bg-muted text-[11px] font-mono text-foreground overflow-x-auto">
                      {s.code}
                    </pre>
                  </div>
                ))}
              </div>

              <div className="p-4 rounded-lg bg-warning/5 border border-warning/20">
                <p className="text-xs font-semibold text-foreground flex items-center gap-2">
                  <Shield className="h-4 w-4 text-warning" /> Security Notes
                </p>
                <ul className="mt-2 space-y-1 text-[11px] text-muted-foreground">
                  <li>• All endpoints enforce Row-Level Security — users only see their own data</li>
                  <li>• Tokens expire after 1 hour and are auto-refreshed by the client SDK</li>
                  <li>• API keys should never be stored in mobile app source code</li>
                  <li>• Use secure storage (Keychain on iOS, EncryptedSharedPreferences on Android)</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Mobile Setup ── */}
        <TabsContent value="mobile" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2">
                <Smartphone className="h-4 w-4 text-warning" /> Capacitor Setup (Android & iOS)
              </CardTitle>
              <CardDescription className="text-xs">Build native Android and iOS apps from this codebase</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {[
                { step: "1", title: "Export & Clone", code: "git clone <your-github-repo>\ncd accoupro\nnpm install" },
                { step: "2", title: "Add Platforms", code: "npx cap add android\nnpx cap add ios" },
                { step: "3", title: "Build & Sync", code: "npm run build\nnpx cap sync" },
                { step: "4", title: "Run on Device", code: "# Android (requires Android Studio)\nnpx cap run android\n\n# iOS (requires Xcode on Mac)\nnpx cap run ios" },
                { step: "5", title: "Hot Reload (Dev)", code: `# capacitor.config.ts already configured with:\nserver: {\n  url: "https://cb2bd23d-3226-4e6f-b573-d11a700fbdf9.lovableproject.com?forceHideBadge=true",\n  cleartext: true\n}` },
              ].map((s) => (
                <div key={s.step} className="flex gap-3">
                  <span className="w-7 h-7 rounded-full bg-warning/10 text-warning text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">{s.step}</span>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-foreground mb-1">{s.title}</p>
                    <pre className="p-3 rounded-lg bg-muted text-[11px] font-mono text-foreground overflow-x-auto border border-border">
                      {s.code}
                    </pre>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Cpu className="h-4 w-4 text-primary" /> API Client Usage (Mobile)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <pre className="p-4 rounded-lg bg-muted text-[11px] font-mono text-foreground overflow-x-auto border border-border whitespace-pre-wrap">
{`import { accouProApi } from "@/lib/api/client";

// Fetch invoices
const { data } = await accouProApi.invoices.list({ type: "Sales" });

// Record a payment
await accouProApi.payments.record({
  invoice_id: "inv_123",
  amount: 47200,
  method: "UPI"
});

// Generate E-Invoice
await accouProApi.gst.generateEInvoice("inv_123");

// Get reports
const report = await accouProApi.reports.profitLoss("2025-04-01", "2026-03-31");

// Get current user
const { data: user } = await accouProApi.users.me();`}
              </pre>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── API Tester ── */}
        <TabsContent value="test" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2">
                <Play className="h-4 w-4 text-accent" /> API Playground
              </CardTitle>
              <CardDescription className="text-xs">Test API endpoints directly from the browser</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Select value={testMethod} onValueChange={setTestMethod}>
                  <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="GET">GET</SelectItem>
                    <SelectItem value="POST">POST</SelectItem>
                    <SelectItem value="PUT">PUT</SelectItem>
                  </SelectContent>
                </Select>
                <div className="flex-1 flex items-center gap-0 border border-border rounded-md overflow-hidden">
                  <span className="px-2 py-2 bg-muted text-[10px] font-mono text-muted-foreground whitespace-nowrap">
                    {baseUrl}
                  </span>
                  <Input
                    value={testEndpoint}
                    onChange={(e) => setTestEndpoint(e.target.value)}
                    className="border-0 rounded-none text-sm font-mono"
                    placeholder="/invoices"
                  />
                </div>
                <Button onClick={handleTest} disabled={testing}>
                  <Play className="h-4 w-4 mr-1" /> {testing ? "Testing..." : "Send"}
                </Button>
              </div>

              <div className="p-2 rounded bg-muted text-[10px] font-mono text-muted-foreground flex items-center gap-2">
                <Lock className="h-3 w-3" /> Authorization: Bearer {"<"}auto-injected from session{">"}
              </div>

              {testResult && (
                <div className="space-y-2">
                  <p className="text-xs font-medium text-foreground">Response:</p>
                  <pre className="p-4 rounded-lg bg-background border border-border text-[11px] font-mono text-foreground overflow-x-auto max-h-[300px]">
                    {testResult}
                  </pre>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="p-4 rounded-lg bg-muted/30 border border-border">
            <p className="text-xs font-semibold text-foreground mb-2">Quick Test Endpoints:</p>
            <div className="flex flex-wrap gap-2">
              {["/invoices", "/payments", "/gst", "/reports", "/users/me", "/gst/validate/27AABCU9603R1ZM"].map((ep) => (
                <Button key={ep} variant="outline" size="sm" className="text-[10px] font-mono h-7"
                  onClick={() => { setTestEndpoint(ep); setTestMethod("GET"); }}>
                  GET {ep}
                </Button>
              ))}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
